import React, { type ReactNode } from "react";
import "./RequestConfirmation.css";
import { ICON_BASE_PATH } from "@/app/utils/config";

type ActionType =
  | "suspend"
  | "delete"
  | "reactivate"
  | "resent"
  | "help"
  | "submitted"
  | "reset"
  | "resend";

type ModalWidth = "small" | "medium" | "large";

interface RequestConfirmationProps {
  onClose?: () => void;
  // actionType is now optional. When omitted, the component becomes fully
  // custom-driven via title/message/requiredContent/icon/children props.
  actionType?: ActionType;
  onConfirm: (actionType?: ActionType) => void;
  errorCode?: string | number | null;
  getActionText?: () => string;
  getActionMessage?: () => string;
  title?: string;
  message?: string;
  // New: lets fully-custom (no actionType) usages supply their own icon.
  icon?: string | null;
  requiredContent?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  closeLabel?: string;
  onlyClose?: boolean;
  children?: ReactNode;
  width?: ModalWidth;
}

const ACTION_CONFIG: Record<
  ActionType,
  { title: string; message: string; icon: string | null }
> = {
  suspend: {
    title: "Suspend Request",
    message: "Are you sure you want to suspend this user?",
    icon: `${ICON_BASE_PATH}suspend_icon.svg`,
  },
  delete: {
    title: "Delete Request",
    message: "Are you sure you want to delete this item?",
    icon: `${ICON_BASE_PATH}deleteicon.svg`,
  },
  reactivate: {
    title: "Reactivate Request",
    message: "Are you sure you want to reactivate this user?",
    icon: `${ICON_BASE_PATH}resendicon.svg`,
  },
  resent: {
    title: "Resend Request",
    message: "Are you sure you want to resend the invitation?",
    icon: `${ICON_BASE_PATH}resendicon.svg`,
  },
  help: {
    title: "Help",
    message: "Need assistance? Please confirm to proceed.",
    icon: `${ICON_BASE_PATH}interrogation-mark.svg`,
  },
  submitted: {
    title: "Submitted",
    message: "Your request has been successfully submitted.",
    icon: `${ICON_BASE_PATH}round_tick.svg`,
  },
  reset: {
    title: "Reset",
    message: "Are you sure you want to reset this form?",
    icon: `${ICON_BASE_PATH}suspend_icon.svg`,
  },
  resend: {
    title: "Resend Request Via",
    message: "Choose how you want to resend the Pay Request.",
    icon: null,
  },
};

// Fallback config used whenever actionType is not provided — every field
// stays empty/null unless the caller supplies title/message/icon props.
const EMPTY_CONFIG = { title: "", message: "", icon: null as string | null };

export const RequestConfirmation: React.FC<RequestConfirmationProps> = ({
  onClose,
  actionType,
  onConfirm,
  errorCode,
  getActionText,
  getActionMessage,
  title,
  message,
  icon,
  requiredContent,
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  closeLabel = "Close",
  onlyClose,
  children,
  width = "medium",
}) => {
  // Only look up ACTION_CONFIG when actionType is actually given.
  const config = actionType ? ACTION_CONFIG[actionType] : EMPTY_CONFIG;

  const resolvedIcon = icon !== undefined ? icon : config.icon;

  const actionText = title ?? (getActionText ? getActionText() : config.title);

  const actionMessage =
    requiredContent !== undefined
      ? null
      : (message ?? (getActionMessage ? getActionMessage() : config.message));

  const getErrorMessage = (code?: string | number | null) => {
    switch (code) {
      case 400:
      case "400":
        return "Bad request. Please check the submitted details.";
      case 401:
      case "401":
        return "Unauthorized action. Please login again.";
      case 403:
      case "403":
        return "You do not have permission to perform this action.";
      case 404:
      case "404":
        return "Requested resource not found.";
      case 500:
      case "500":
        return "Something went wrong. Please try again later.";
      default:
        return "Unable to process your request at the moment.";
    }
  };

  // Show only Close when:
  //   • caller passes onlyClose={true}, OR
  //   • actionType is "help" / "submitted" (original behaviour)
  // requiredContent alone no longer forces onlyClose — callers can now
  // pass requiredContent + Cancel/Confirm labels freely.
  const showOnlyClose =
    onlyClose === true || actionType === "help" || actionType === "submitted";

  return (
    <div className="gx-request">
      <div className={`gx-request-box gx-request-box--${width}`}>
        <div>
          <div className="gx-request-header">
            <div className="flex">
              {resolvedIcon && (
                <img
                  className="mr-1"
                  src={resolvedIcon}
                  alt={actionType ?? "icon"}
                />
              )}
              <div className="ml-2">
                <p className="gx-p18-b">{actionText}</p>
                {requiredContent ? (
                  <div className="gx-request-message">{requiredContent}</div>
                ) : (
                  actionMessage && (
                    <p className="gx-p14-md !text-[#535862]">{actionMessage}</p>
                  )
                )}
              </div>
            </div>
          </div>

          {errorCode && (
            <p className="gx-f10-r-m">{getErrorMessage(errorCode)}</p>
          )}
        </div>
        {children && <div className="mt-4">{children}</div>}

        {/* Cancel + Confirm footer */}
        {!showOnlyClose && (
          <div className="gx-request-footer">
            <button className="cancel" onClick={onClose}>
              <span className="gx-p16-b"> {cancelLabel}</span>
            </button>
            <button className="confirm " onClick={() => onConfirm(actionType)}>
              <span className="gx-p16-b">{confirmLabel}</span>
            </button>
          </div>
        )}

        {/* Close-only footer */}
        {showOnlyClose && onClose && (
          <div className="gx-request-close-f">
            <button className="close gx-p16-b" onClick={onClose}>
              {closeLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
